import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Grid, useMediaQuery, useTheme } from '@mui/material';
import React from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import { omit } from 'lodash';
import { usePostHog } from 'posthog-js/react';
import { KeyedMutator } from 'swr/_internal';
import { AccountData, AccountService, OrderPageData, WorkingDaysData } from '../../../api';
import { OrderPageUpdate } from '../../OrderPageSettingsPage';
import { UseAuthPermissions } from '../../../session/UseAuthPermissions';
import { WorkingDays } from '../../workingSchedule/WorkingDays';
import { SectionSaveRegistrar } from '../../saveHeader/SectionSaveRegistrar';
import { diffWorkingDays } from '../../saveHeader/diffBuilders';
import { SettingsPageTabs } from '../../SettingsPageTabs';

type PricingSectionProps = {
  orderPage: OrderPageData;
  orderPageInitialValues: OrderPageUpdate;
  updateLocalAccount: KeyedMutator<AccountData>;
  accountWorkingDays: WorkingDaysData;
};

export type WorkingDaysFormValues = {
  working_days: WorkingDaysData;
};

export const WorkingDaysSection = ({
  orderPage,
  orderPageInitialValues,
  updateLocalAccount,
  accountWorkingDays,
}: PricingSectionProps) => {
  const { t } = useTranslation();
  const { readOnly } = UseAuthPermissions();
  const posthog = usePostHog();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const validationSchema = yup.object().shape({});

  const initialValues: WorkingDaysFormValues = {
    working_days: orderPage?.working_days ? orderPage.working_days : accountWorkingDays,
  };

  const handleSubmit = async (values: WorkingDaysFormValues, { setStatus }: FormikHelpers<WorkingDaysFormValues>) => {
    try {
      posthog.capture('Editing settings', { section: 'workingDaysSection', settings: values });
      const updatedAccount = await AccountService.updateOrderPage(
        omit(
          {
            ...orderPageInitialValues,
            duration: (orderPageInitialValues.duration || 0) * 60,
            ...values,
            id: orderPage.id,
          },
          'availability_settings.restrictions',
          'objects',
        ),
      );
      await updateLocalAccount(updatedAccount);
    } catch (e) {
      setStatus({ error: (e as Error)?.message || t('saveHeader.errorGeneric') });
      throw e;
    }
  };

  const getChanges = (values: WorkingDaysFormValues, iv: WorkingDaysFormValues) =>
    diffWorkingDays(iv.working_days, values.working_days, t);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}>
      {(formik) => {
        const { values, isSubmitting, setFieldValue } = formik;
        return (
          <Form onSubmit={formik.handleSubmit}>
            <SectionSaveRegistrar tabKey={SettingsPageTabs.WORKING_DAYS} getChanges={getChanges} disabled={readOnly} />
            <Grid
              container
              flexDirection={isSmallScreen ? 'column' : 'row'}
              justifyContent="space-between"
              flexWrap="nowrap"
              alignItems="flex-start">
              <WorkingDays
                initialWorkingDays={initialValues.working_days}
                workingDays={values.working_days}
                setFieldValue={setFieldValue}
                isSubmitting={isSubmitting}
                readOnly={readOnly}
              />
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
